import AntdMessage from 'antd/es/message'
import type { MessageInstance } from 'antd/es/message/interface'
import AntdNotification from 'antd/es/notification'
import type { NotificationInstance } from 'antd/es/notification/interface'

import { getDesignMode } from 'src/design-mode/store'

import {
  noticeKeyOf,
  sonnerMessage,
  sonnerNotification,
} from './sonner/adapter'

export type { MessageInstance }
export type { NotificationInstance }
export type {
  ArgsProps,
  JointContent,
  MessageType,
  NoticeType,
} from './sonner/adapter'

/**
 * Toast/notification seam.
 *
 * Import `{ message }` / `{ notification }` from here (or from `src/antd`,
 * which shadows the same two names) — never from `antd` directly. ~460 files
 * already do, so everything below reaches every call site with no call-site
 * change at all.
 *
 * Leaf and not the `src/antd` barrel on purpose (PRO-130): the callers are
 * boot-static (`src/api/auth`, `src/components/graphql/*`), and the barrel's
 * `export * from 'antd'` would weld antd's whole component surface onto the
 * entry chunk. Everything this file imports has to stay leaf-safe for the same
 * reason — which is why `./sonner/adapter` names no engine and `sonner` itself
 * is reached only through `./Toaster`'s `React.lazy`.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS: A CALL-TIME DISPATCHER OVER TWO BACKENDS
 *
 * `message` and `notification` are Proxies. For every INSTANCE method (`open`,
 * `success`, `error`, `info`, `warning`, `loading`, `destroy`) they resolve a
 * backend when the method is reached, in this order:
 *
 *   1. SONNER, if the design toggle says `shadcn` AND its chunk has landed
 *      (`./sonner/adapter`, which returns `null`/`false` when it has not).
 *      This is the "New design" rendering; the host is `./Toaster`.
 *   2. THE BRIDGED ANTD INSTANCE, if one has been published (below).
 *   3. THE PLAIN ANTD STATIC, otherwise.
 *
 * The toggle is read at CALL time rather than through a hook because there is
 * nothing rendered to subscribe — this is an imperative API, and it is the
 * same shape `src/antd/Modal`'s `Modal.confirm` statics use for the same
 * reason. On the antd rendering the dispatch is a no-op: step 1 declines and
 * the file behaves exactly as it did before Sonner existed, which is what
 * keeps "Old design" byte-identical.
 *
 * Falling THROUGH to antd rather than dropping the call is deliberate. Between
 * app boot and the Toaster's chunk resolving there is a brief window in which
 * a shadcn-mode user can fire a toast; showing them one antd notice is a
 * cosmetic surprise, and swallowing an error message is a bug report titled
 * "saving does nothing".
 *
 * That fallback leaves one thing to clean up, and `withKeyedAntdCleanup` is
 * where. A KEYED notice is an identity a later call REPLACES — the
 * `message.loading({ key, duration: 0 })` → `message.success({ key })`
 * progress pattern in `RoadsNotTaken`. If the first half lands in that window
 * it is an antd notice, the second half is a Sonner toast, and the two
 * backends cannot see each other: the antd one is `duration: 0`, so it never
 * auto-closes and no later `destroy(key)` reaches it. So whenever the Sonner
 * branch emits under a key, the antd holder is told to destroy that same key.
 * On a key antd never showed it is a no-op.
 *
 * `destroy` itself is the other half of that, and it is not the same case:
 * `withAntdDestroy` sends EVERY destroy to both backends, because the live
 * path (`WsDisconnectNotification`) never emits a second time under its key —
 * it just destroys it — so the emit side alone would strand the notice.
 *
 * ---------------------------------------------------------------------------
 * WHY THE ANTD PATH IS A DELEGATING PROXY AND NOT A PLAIN RE-EXPORT
 *
 * antd's static `message.success(...)` / `notification.open(...)` render into
 * a detached DOM root created the first time they are called — outside
 * `AntdI18n`'s `<ConfigProvider>` and its nested `<App>` — so a toast never
 * reads the app's `ConfigProvider` theme: it stays on antd's default font
 * token and default (light) palette even on a dark tenant — the two font
 * dispatch points next to `GEIST_FONT_FAMILY` in `src/styles/font.ts` cannot
 * reach it either, which is why that file points back here.
 *
 * `App.useApp()` is antd's own fix — it returns `message`/`notification`
 * instances that render inside whatever `<App>` they were requested from,
 * so they pick up that `<App>`'s ConfigProvider context like any other
 * component. `AntdStaticsBridge` (mounted once, inside `AntdI18n`'s `<App>`)
 * calls that hook and publishes the resulting instances here via
 * `setAntdStaticsBridge`. Step 3 above exists because `App.useApp()` outside
 * an `<App>` returns antd's default context value, which is an EMPTY
 * `message`/`notification` object rather than a throw, so a bridge mounted in
 * the wrong place would otherwise turn every call site into a `TypeError`
 * instead of degrading to the unthemed static.
 *
 * `useMessage`/`useNotification`/`config`/the internal panel export are never
 * delegated — neither `App.useApp()`'s return value nor the Sonner adapter has
 * them — so those keep resolving to the plain antd static. That is a KNOWN
 * RESIDUAL, not an oversight: `src/utils/antdMessageContext.tsx` builds its
 * store on `message.useMessage()`, so its six call sites keep antd notices in
 * both designs. A switched page is mixed by construction anyway (antd renders
 * its own Checkbox inside `Table`'s `rowSelection`), and that file has a
 * cleanup task of its own — `fe-remove-antd-message-context` — which is where
 * moving those six belongs.
 */

const MESSAGE_INSTANCE_METHODS = new Set([
  'open',
  'success',
  'error',
  'info',
  'warning',
  'loading',
  'destroy',
])

const NOTIFICATION_INSTANCE_METHODS = new Set([
  'open',
  'success',
  'error',
  'info',
  'warning',
  'destroy',
])

let bridgedMessage: MessageInstance | null = null
let bridgedNotification: NotificationInstance | null = null

/** Published by `AntdStaticsBridge` only; not for call sites to use directly. */
export const setAntdStaticsBridge = (
  next: {
    message: MessageInstance
    notification: NotificationInstance
  } | null
): void => {
  bridgedMessage = next?.message ?? null
  bridgedNotification = next?.notification ?? null
}

/** Any instance method, in the shape the Proxy hands one back. */
type NoticeMethod = (...args: never[]) => unknown

/**
 * A Sonner method returns `null` precisely when its engine has not loaded, so
 * a wrapper that sees that value re-runs the same call against antd. The
 * wrapper — not the caller — is what makes step 1's "declines" and step 2/3's
 * "handles it" one non-duplicating decision: exactly one backend ever emits a
 * notice for one call.
 */
const withAntdFallback =
  (sonnerFn: NoticeMethod, antdFn: NoticeMethod): NoticeMethod =>
  (...args) => {
    const emitted = sonnerFn(...args)
    return emitted === null ? antdFn(...args) : emitted
  }

/**
 * A keyed Sonner emit also closes the antd notice the pre-engine fallback may
 * have painted under that key; see the header. Unkeyed calls do nothing extra,
 * and a Sonner decline (`null`) still falls through untouched.
 */
const withKeyedAntdCleanup =
  (
    sonnerFn: NoticeMethod,
    getAntdDestroy: () => NoticeMethod | null
  ): NoticeMethod =>
  (...args) => {
    const emitted = sonnerFn(...args)
    if (emitted === null) return null
    const key = noticeKeyOf(args)
    if (key !== undefined) getAntdDestroy()?.(key as never)
    return emitted
  }

/**
 * `destroy` reaches BOTH backends, always — the emit-side twin above only
 * helps when a SECOND keyed call arrives under the same key, and the live
 * path has none: `WsDisconnectNotification` paints one `duration: 0`
 * "Disconnected" notice and later calls `message.destroy(key)` on it. If that
 * notice was the pre-engine antd fallback's, a Sonner-only dismiss leaves it
 * on screen for the rest of the session.
 *
 * The same-API antd holder is cleared for a BARE `destroy()` too: this proxy
 * IS the one API, so `message.destroy()` empties antd's message holder and
 * never touches the notification one — antd's own two-holder behaviour, which
 * is exactly what `dismissFor` reproduces on the Sonner side. A keyed destroy
 * on a key antd never showed is a no-op.
 */
const withAntdDestroy =
  (sonnerFn: NoticeMethod, antdFn: NoticeMethod): NoticeMethod =>
  (...args) => {
    sonnerFn(...args)
    return antdFn(...args)
  }

const delegate = <F extends object>(
  fallback: F,
  instanceMethods: Set<string>,
  getInstance: () => Partial<F> | null,
  sonner: Record<string, NoticeMethod>
): F =>
  new Proxy(fallback, {
    get(target, prop, receiver) {
      // Steps 2 and 3, resolved exactly as they were before Sonner existed:
      // the bridged instance's method bound to it, else whatever the antd
      // static carries, untouched.
      const resolveAntd = (name: string): NoticeMethod | null => {
        const instance = getInstance()
        const bridged = instance
          ? Reflect.get(instance as object, name, instance)
          : undefined
        if (typeof bridged === 'function') {
          return bridged.bind(instance) as NoticeMethod
        }
        const original = Reflect.get(target, name, receiver)
        return typeof original === 'function'
          ? (original as NoticeMethod)
          : null
      }

      if (typeof prop === 'string' && instanceMethods.has(prop)) {
        const antd = resolveAntd(prop)

        // Step 1. Off the shadcn rendering this is `undefined` and the whole
        // dispatch collapses back to `antd` — the byte-identical path.
        const shadcn = getDesignMode() === 'shadcn' ? sonner[prop] : undefined
        if (shadcn) {
          if (prop === 'destroy') {
            return antd ? withAntdDestroy(shadcn, antd) : shadcn
          }
          const emit = withKeyedAntdCleanup(shadcn, () =>
            resolveAntd('destroy')
          )
          return antd ? withAntdFallback(emit, antd) : emit
        }
        if (antd) return antd
      }
      return Reflect.get(target, prop, receiver)
    },
  })

export const message: typeof AntdMessage = delegate(
  AntdMessage,
  MESSAGE_INSTANCE_METHODS,
  () => bridgedMessage,
  sonnerMessage as unknown as Record<string, NoticeMethod>
)

export const notification: typeof AntdNotification = delegate(
  AntdNotification,
  NOTIFICATION_INSTANCE_METHODS,
  () => bridgedNotification,
  sonnerNotification as unknown as Record<string, NoticeMethod>
)
